import React, { useState, useEffect } from 'react';
import apiUsageService from '../services/apiUsageService';

const ApiUsageMonitor = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 데이터 상태
  const [summary, setSummary] = useState(null);
  const [categoryStats, setCategoryStats] = useState([]);
  const [endpointStats, setEndpointStats] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [costlyUsage, setCostlyUsage] = useState({ apis: [], topUsers: [] });
  const [recentLogs, setRecentLogs] = useState([]);
  const [errorStats, setErrorStats] = useState({ byStatusCode: [], byEndpoint: [] });

  // 필터 상태
  const [daysFilter, setDaysFilter] = useState(7);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('');

  // 데이터 로드
  const loadSummary = async () => {
    try {
      const result = await apiUsageService.getSummary();
      setSummary(result.data);
    } catch (err) {
      console.error('Summary error:', err);
    }
  };

  const loadCategoryStats = async () => {
    try {
      const result = await apiUsageService.getByCategory(daysFilter);
      setCategoryStats(result.data);
    } catch (err) {
      console.error('Category stats error:', err);
    }
  };

  const loadEndpointStats = async () => {
    try {
      const result = await apiUsageService.getByEndpoint(daysFilter, categoryFilter || null, 30);
      setEndpointStats(result.data);
    } catch (err) {
      console.error('Endpoint stats error:', err);
    }
  };

  const loadDailyTrend = async () => {
    try {
      const result = await apiUsageService.getDailyTrend(14, categoryFilter || null);
      setDailyTrend(result.data);
    } catch (err) {
      console.error('Daily trend error:', err);
    }
  };

  const loadCostlyUsage = async () => {
    try {
      const result = await apiUsageService.getCostlyUsage(30);
      setCostlyUsage(result.data);
    } catch (err) {
      console.error('Costly usage error:', err);
    }
  };

  const loadRecentLogs = async () => {
    try {
      const result = await apiUsageService.getRecentLogs(
        50,
        categoryFilter || null,
        logStatusFilter || null
      );
      setRecentLogs(result.data);
    } catch (err) {
      console.error('Recent logs error:', err);
    }
  };

  const loadErrorStats = async () => {
    try {
      const result = await apiUsageService.getErrorStats(daysFilter);
      setErrorStats(result.data);
    } catch (err) {
      console.error('Error stats error:', err);
    }
  };

  // 초기 로드
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadSummary();
        await loadCategoryStats();
      } catch (err) {
        setError('데이터 로드 실패');
      }
      setLoading(false);
    };
    loadData();
  }, []);

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    const loadTabData = async () => {
      switch (activeTab) {
        case 'summary':
          await loadCategoryStats();
          break;
        case 'category':
          await loadCategoryStats();
          break;
        case 'endpoint':
          await loadEndpointStats();
          break;
        case 'trend':
          await loadDailyTrend();
          break;
        case 'costly':
          await loadCostlyUsage();
          break;
        case 'logs':
          await loadRecentLogs();
          break;
        case 'errors':
          await loadErrorStats();
          break;
        default:
          break;
      }
    };
    loadTabData();
  }, [activeTab, daysFilter, categoryFilter, logStatusFilter]);

  // 날짜 포맷
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 숫자 포맷
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  // 바이트 포맷
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading && !summary) {
    return <div className="text-center py-12 text-gray-600 text-[15px] font-medium">로딩 중...</div>;
  }

  if (error) {
    return <div className="text-center py-12 text-red-600 text-[15px] font-medium">{error}</div>;
  }

  return (
    <div className="p-0">
      {/* 요약 통계 카드 */}
      {summary && (
        <div className="grid grid-cols-3 gap-4 mb-6 max-md:grid-cols-1 max-md:gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 border-t-4 border-t-blue-500 max-md:p-4">
            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">오늘</div>
            <div className="text-[32px] font-bold text-gray-900 leading-tight tracking-tight max-md:text-[28px]">{formatNumber(summary.today?.total_calls)}</div>
            <div className="flex justify-center gap-4 mt-4 text-xs font-medium">
              <span className="text-emerald-600">✓ {summary.today?.success_calls || 0}</span>
              <span className="text-red-600">✗ {summary.today?.error_calls || 0}</span>
            </div>
            <div className="text-xs text-gray-600 mt-2 font-medium">평균 {summary.today?.avg_response_time}ms</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 border-t-4 border-t-emerald-500 max-md:p-4">
            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">이번 주</div>
            <div className="text-[32px] font-bold text-gray-900 leading-tight tracking-tight max-md:text-[28px]">{formatNumber(summary.week?.total_calls)}</div>
            <div className="flex justify-center gap-4 mt-4 text-xs font-medium">
              <span className="text-emerald-600">✓ {formatNumber(summary.week?.success_calls)}</span>
              <span className="text-red-600">✗ {summary.week?.error_calls || 0}</span>
            </div>
            <div className="text-xs text-gray-600 mt-2 font-medium">사용자 {summary.week?.unique_users || 0}명</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 border-t-4 border-t-violet-500 max-md:p-4">
            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">이번 달</div>
            <div className="text-[32px] font-bold text-gray-900 leading-tight tracking-tight max-md:text-[28px]">{formatNumber(summary.month?.total_calls)}</div>
            <div className="flex justify-center gap-4 mt-4 text-xs font-medium">
              <span className="text-emerald-600">✓ {formatNumber(summary.month?.success_calls)}</span>
              <span className="text-red-600">✗ {summary.month?.error_calls || 0}</span>
            </div>
            <div className="text-xs text-gray-600 mt-2 font-medium">사용자 {summary.month?.unique_users || 0}명</div>
          </div>
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 pb-0 overflow-x-auto">
        {[
          { id: 'summary', label: '요약' },
          { id: 'category', label: '카테고리별' },
          { id: 'endpoint', label: '엔드포인트별' },
          { id: 'costly', label: '비용 API' },
          { id: 'logs', label: '최근 로그' },
          { id: 'errors', label: '에러' }
        ].map((tab) => (
          <button
            key={tab.id}
            className={`py-4 px-6 bg-transparent border-none border-b-2 border-transparent -mb-px text-[15px] font-medium text-gray-600 cursor-pointer transition-all whitespace-nowrap min-h-[44px] max-md:flex-shrink-0 max-md:p-4 max-md:text-xs ${
              activeTab === tab.id
                ? 'bg-transparent text-blue-500 border-b-blue-500 font-bold'
                : 'hover:bg-slate-50 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <select
          value={daysFilter}
          onChange={(e) => setDaysFilter(parseInt(e.target.value))}
          className="py-2 px-4 border border-gray-200 rounded-md text-xs font-medium bg-white cursor-pointer transition-all min-h-[44px] hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 max-md:flex-1 max-md:min-w-[140px]"
        >
          <option value={1}>오늘</option>
          <option value={7}>최근 7일</option>
          <option value={14}>최근 14일</option>
          <option value={30}>최근 30일</option>
        </select>

        {(activeTab === 'endpoint' || activeTab === 'logs') && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="py-2 px-4 border border-gray-200 rounded-md text-xs font-medium bg-white cursor-pointer transition-all min-h-[44px] hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 max-md:flex-1 max-md:min-w-[140px]"
          >
            <option value="">전체 카테고리</option>
            <option value="인증">인증</option>
            <option value="사용자">사용자</option>
            <option value="AI">AI</option>
            <option value="커뮤니티">커뮤니티</option>
            <option value="파일">파일</option>
            <option value="알림">알림</option>
            <option value="관리자">관리자</option>
          </select>
        )}

        {activeTab === 'logs' && (
          <select
            value={logStatusFilter}
            onChange={(e) => setLogStatusFilter(e.target.value)}
            className="py-2 px-4 border border-gray-200 rounded-md text-xs font-medium bg-white cursor-pointer transition-all min-h-[44px] hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 max-md:flex-1 max-md:min-w-[140px]"
          >
            <option value="">전체 상태</option>
            <option value="success">성공만</option>
            <option value="error">에러만</option>
          </select>
        )}
      </div>

      {/* 탭 콘텐츠 */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {/* 요약 탭 */}
        {activeTab === 'summary' && categoryStats.length > 0 && (
          <div className="p-6 max-md:p-4">
            <h3 className="text-[15px] font-bold m-0 mb-6 text-gray-900">카테고리별 API 호출</h3>
            <div className="flex flex-col gap-4">
              {categoryStats.map((cat, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-[120px] flex justify-between text-xs max-md:w-[80px] max-md:text-[11px]">
                    <span className="text-gray-900 font-medium">{cat.category}</span>
                    <span className="text-gray-600 font-medium">{formatNumber(cat.total_calls)}</span>
                  </div>
                  <div className="flex-1 h-2 bg-slate-50 rounded-md overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-md transition-all duration-400"
                      style={{ width: `${cat.percentage}%` }}
                    />
                  </div>
                  <span className="w-12 text-right text-xs text-gray-600 font-medium max-md:w-10 max-md:text-[11px]">{cat.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 카테고리별 탭 */}
        {activeTab === 'category' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">카테고리</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">총 호출</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">성공</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">에러</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">평균 응답</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">사용자</th>
                </tr>
              </thead>
              <tbody>
                {categoryStats.map((cat, idx) => (
                  <tr key={idx} className="transition-colors hover:bg-slate-50">
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">
                      <span className="inline-block py-1 px-2 bg-blue-50 text-blue-700 rounded-md text-xs font-bold uppercase tracking-wide">{cat.category}</span>
                    </td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{formatNumber(cat.total_calls)}</td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs text-emerald-600 font-medium max-md:p-2 max-md:text-[11px]">{formatNumber(cat.success_calls)}</td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs text-red-600 font-medium max-md:p-2 max-md:text-[11px]">{cat.error_calls}</td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{cat.avg_response_time}ms</td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{cat.unique_users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 엔드포인트별 탭 */}
        {activeTab === 'endpoint' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">엔드포인트</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">메소드</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">호출</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">성공률</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">평균 응답</th>
                </tr>
              </thead>
              <tbody>
                {endpointStats.map((ep, idx) => {
                  const successRate = ep.total_calls > 0
                    ? Math.round((ep.success_calls / ep.total_calls) * 100)
                    : 0;
                  return (
                    <tr key={idx} className={`transition-colors hover:bg-slate-50 ${ep.is_costly ? 'bg-yellow-50' : ''}`}>
                      <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">
                        <code className="bg-slate-50 py-1 px-2 rounded-md text-xs font-mono font-medium">{ep.endpoint}</code>
                        {ep.is_costly && <span className="ml-2 text-xs">💰</span>}
                      </td>
                      <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">
                        <span className={`inline-block py-1 px-2 rounded-md text-xs font-bold font-mono uppercase ${
                          ep.method === 'GET' ? 'bg-blue-50 text-blue-700' :
                          ep.method === 'POST' ? 'bg-green-50 text-green-700' :
                          ep.method === 'PUT' ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }`}>{ep.method}</span>
                      </td>
                      <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{formatNumber(ep.total_calls)}</td>
                      <td className={`p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px] ${successRate < 90 ? 'text-amber-600 font-medium' : ''}`}>
                        {successRate}%
                      </td>
                      <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{ep.avg_response_time}ms</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 비용 API 탭 */}
        {activeTab === 'costly' && (
          <div className="p-6 max-md:p-4">
            <div className="mb-8">
              <h3 className="text-[15px] font-bold m-0 mb-6 text-gray-900">비용 API 사용량</h3>
              {costlyUsage.apis.length === 0 ? (
                <p className="text-center py-12 text-gray-600 text-[15px] bg-slate-50 rounded-md m-6">비용 API 사용 기록이 없습니다.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">엔드포인트</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">호출 수</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">성공</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">에러</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">사용자</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costlyUsage.apis.map((api, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-slate-50">
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]"><code className="bg-slate-50 py-1 px-2 rounded-md text-xs font-mono font-medium">{api.endpoint}</code></td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{api.total_calls}</td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs text-emerald-600 font-medium max-md:p-2 max-md:text-[11px]">{api.success_calls}</td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs text-red-600 font-medium max-md:p-2 max-md:text-[11px]">{api.error_calls}</td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{api.unique_users}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-[15px] font-bold m-0 mb-6 text-gray-900">상위 사용자</h3>
              {costlyUsage.topUsers.length === 0 ? (
                <p className="text-center py-12 text-gray-600 text-[15px] bg-slate-50 rounded-md m-6">데이터가 없습니다.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">사용자</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">이메일</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">호출 수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costlyUsage.topUsers.map((user, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-slate-50">
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{user.name}</td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{user.email}</td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{user.total_calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* 최근 로그 탭 */}
        {activeTab === 'logs' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">시간</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">엔드포인트</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">메소드</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">상태</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">응답시간</th>
                  <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">사용자</th>
                </tr>
              </thead>
              <tbody>
                {recentLogs.map((log, idx) => (
                  <tr key={idx} className={`transition-colors hover:bg-slate-50 ${log.status_code >= 400 ? 'bg-red-50' : ''}`}>
                    <td className="p-4 text-left border-b border-gray-200 text-xs whitespace-nowrap text-gray-600 font-medium max-md:p-2 max-md:text-[11px]">{formatDate(log.created_at)}</td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]"><code className="bg-slate-50 py-1 px-2 rounded-md text-xs font-mono font-medium">{log.endpoint}</code></td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">
                      <span className={`inline-block py-1 px-2 rounded-md text-xs font-bold font-mono uppercase ${
                        log.method === 'GET' ? 'bg-blue-50 text-blue-700' :
                        log.method === 'POST' ? 'bg-green-50 text-green-700' :
                        log.method === 'PUT' ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700'
                      }`}>{log.method}</span>
                    </td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">
                      <span className={`inline-block py-1 px-4 rounded-xl text-xs font-medium ${
                        log.status_code < 400 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {log.status_code}
                      </span>
                    </td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{log.response_time}ms</td>
                    <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{log.user_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 에러 탭 */}
        {activeTab === 'errors' && (
          <div className="p-6 max-md:p-4">
            <div className="mb-8">
              <h3 className="text-[15px] font-bold m-0 mb-6 text-gray-900">상태 코드별 에러</h3>
              {errorStats.byStatusCode.length === 0 ? (
                <p className="text-center py-12 text-gray-600 text-[15px] bg-slate-50 rounded-md m-6">에러가 없습니다.</p>
              ) : (
                <div className="flex gap-4 flex-wrap max-md:gap-2">
                  {errorStats.byStatusCode.map((err, idx) => (
                    <div key={idx} className="flex flex-col items-center py-4 px-6 bg-red-50 rounded-xl border border-red-200 min-w-[100px] transition-all hover:shadow-md hover:-translate-y-0.5 max-md:py-2 max-md:px-4 max-md:min-w-[80px]">
                      <span className="text-xl font-bold text-red-600 tracking-tight max-md:text-lg">{err.status_code}</span>
                      <span className="text-xs text-red-800 mt-1 font-medium">{err.count}건</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-[15px] font-bold m-0 mb-6 text-gray-900">엔드포인트별 에러</h3>
              {errorStats.byEndpoint.length === 0 ? (
                <p className="text-center py-12 text-gray-600 text-[15px] bg-slate-50 rounded-md m-6">에러가 없습니다.</p>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">엔드포인트</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">메소드</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">에러 수</th>
                      <th className="p-4 text-left border-b border-gray-200 text-xs bg-slate-50 font-bold text-gray-600 uppercase tracking-wider max-md:p-2 max-md:text-[11px]">상태 코드</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorStats.byEndpoint.map((err, idx) => (
                      <tr key={idx} className="transition-colors hover:bg-slate-50">
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]"><code className="bg-slate-50 py-1 px-2 rounded-md text-xs font-mono font-medium">{err.endpoint}</code></td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">
                          <span className={`inline-block py-1 px-2 rounded-md text-xs font-bold font-mono uppercase ${
                            err.method === 'GET' ? 'bg-blue-50 text-blue-700' :
                            err.method === 'POST' ? 'bg-green-50 text-green-700' :
                            err.method === 'PUT' ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'
                          }`}>{err.method}</span>
                        </td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs text-red-600 font-medium max-md:p-2 max-md:text-[11px]">{err.error_count}</td>
                        <td className="p-4 text-left border-b border-gray-200 text-xs max-md:p-2 max-md:text-[11px]">{err.status_codes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiUsageMonitor;
